import React from "react";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../../Components/ui/select.tsx";
import { Input } from "../../../../Components/ui/input.tsx";
import { Button } from "../../../../Components/ui/button.tsx";

import { TrashIcon } from "lucide-react";

import { MAP_OPERATOR_TO_FIELD_KIND } from "../../constants/logic.ts";

import { ActionType } from "../../types/zustand.ts";
import { Logic, OPERATOR } from "../../types/logic.ts";
import { FieldIcon } from "../FieldIcon.tsx";
import { getLetterAtIndex, truncate } from "../../utils/string.ts";
import { useFormEditorContext } from "../../../../Contexts/FormEditorContext.jsx";
import { useLogicV2 } from "../../hooks/use-logic-v2.ts";

type RuleProps = {
    id: string;
    index: number;
    groupId: string;
    branchId: string;
};

export function Rule({ id, index, groupId, branchId }: RuleProps) {
    const { fields, logics, selectedField } = useFormEditorContext();
    const { dispatch } = useLogicV2();
    const activeFieldId = selectedField?.id;
    const logic = (logics as Logic[]).find(
        (logic) => logic.fieldId === activeFieldId,
    );

    if (!logic) return null;

    const branch = logic.branches.find((branch) => branch.id === branchId);

    if (!branch) return null;

    const group = branch.groups.find((group) => group.id === groupId);

    if (!group) return null;

    const rule = group.rules.find((rule) => rule.id === id);

    if (!rule) return null;

    // const [sourceId, setSourceId] = useState<string | null>(rule.sourceId);
    // const [operator, setOperator] = useState<OPERATOR | null>(rule.operator);
    // const [expectedValue, setExpectedValue] = useState<string | null>(
    //     rule.expectedValue,
    // );

    const getApplicableFields = () => {
        // This returns all the fields that are positioned before the current active field (including the current active field)
        const activeFieldIndex = fields.findIndex(
            (field) => field.id === activeFieldId,
        );

        return fields
            .slice(0, activeFieldIndex + 1)
            ?.map((field, index) => ({
                ...field,
                index: index + 1,
            }))
            .filter(
                (field) =>
                    field?.type !== "ai_statement" &&
                    field?.type !== "contact_info",
            );
    };

    const getApplicableOperators = () => {
        const sourceField = fields.find((field) => field.id === rule.sourceId);

        if (!sourceField) return [];

        return (
            <Select
                value={rule.operator ?? undefined}
                onValueChange={(value) =>
                    handleOnChange({
                        operator: value as OPERATOR,
                        sourceId: rule.sourceId,
                        expectedValue: rule.expectedValue,
                    })
                }
            >
                <SelectTrigger className="h-9 text-xs py-2 px-3">
                    <SelectValue placeholder="Operator" />
                </SelectTrigger>
                <SelectContent>
                    {MAP_OPERATOR_TO_FIELD_KIND[sourceField.type]?.map(
                        (operator) => (
                            <SelectItem
                                key={operator.value}
                                value={operator.value}
                                className="p-[7px] text-xs"
                            >
                                {operator.label}
                            </SelectItem>
                        ),
                    )}
                </SelectContent>
            </Select>
        );
    };

    const getApplicableOperands = () => {
        const sourceField = fields.find((field) => field.id === rule.sourceId);

        if (!sourceField) return null;

        if (sourceField.type === "multiple_choice")
            return (
                <Select
                    value={rule.expectedValue ?? undefined}
                    onValueChange={(value) =>
                        handleOnChange({
                            sourceId: rule.sourceId,
                            operator: rule.operator,
                            expectedValue: value,
                        })
                    }
                >
                    <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                        {sourceField?.properties?.choices?.map(
                            (choice, index) => (
                                <SelectItem
                                    key={choice.id}
                                    value={choice.label}
                                    className="p-[7px]"
                                >
                                    <div className="flex flex-row items-center gap-2">
                                        <div
                                            className="flex h-6 w-6 items-center justify-center rounded-[2px] border text-center text-xs font-semibold"
                                            style={{
                                                borderRadius: 4,
                                                borderColor: "#C2DCFF",
                                                borderWidth: "1px",
                                                color: "#2563EB",
                                                backgroundColor: "#FFFFFF",
                                            }}
                                        >
                                            <p className="text-xs font-semibold">
                                                {getLetterAtIndex(index)}
                                            </p>
                                        </div>
                                        <p className="text-xs text-[#191B33]">
                                            {choice.label}
                                        </p>
                                    </div>
                                </SelectItem>
                            ),
                        )}
                    </SelectContent>
                </Select>
            );

        return (
            <Input
                value={rule.expectedValue ?? undefined}
                onChange={(e) =>
                    handleOnChange({
                        sourceId: rule.sourceId,
                        operator: rule.operator,
                        expectedValue: e.target.value,
                    })
                }
                className="h-9"
            />
        );
    };

    // useEffect(() => {
    //     if (rule.sourceId && rule.operator && rule.expectedValue) {
    //         handleChangeField();
    //     }
    // }, [rule.sourceId, rule.operator, rule.expectedValue]);

    // const handleChangeField = () => {
    //     dispatch({
    //         type: ActionType.EDIT_RULE,
    //         payload: {
    //             ruleId: id,
    //             sourceId: rule.sourceId,
    //             operator: rule.operator,
    //             expectedValue: rule.expectedValue,
    //         },
    //     });
    // };

    const handleRemoveRule = () => {
        dispatch({
            type: ActionType.REMOVE_RULE,
            payload: {
                ruleId: id,
            },
        });
    };

    const handleOnChange = ({
        sourceId,
        operator,
        expectedValue,
    }: {
        sourceId?: string;
        operator?: OPERATOR;
        expectedValue?: string;
    }) => {
        dispatch({
            type: ActionType.EDIT_RULE,
            payload: {
                ruleId: id,
                sourceId,
                operator,
                expectedValue,
            },
        });
    };

    return (
        <div className="bg-gray-100 rounded-[8px] p-2">
            {index > 0 && (
                <p className="text-left text-sm font-medium text-[#191B33]">
                    AND
                </p>
            )}
            <Select
                value={rule.sourceId}
                onValueChange={(value) => handleOnChange({ sourceId: value })}
            >
                <SelectTrigger className="h-9 mt-2">
                    <SelectValue placeholder="Field" />
                </SelectTrigger>
                <SelectContent className="w-[240px]">
                    {getApplicableFields().map((field, index) => (
                        <SelectItem
                            key={field.id}
                            value={field.id}
                            className="p-[7px]"
                        >
                            <div className="flex flex-row items-center gap-2">
                                <FieldIcon
                                    type={field.type}
                                    index={field.index}
                                    className="h-[22px] w-9 p-[5px] text-[8px]"
                                />
                                <p className="text-xs text-[#191B33]">
                                    {truncate(field.title, 24)}
                                </p>
                            </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <div className="flex flex-row w-full gap-1 mt-1">
                {getApplicableOperators()}
                {getApplicableOperands()}
            </div>
            <div className="flex justify-end w-full mt-1">
                <Button
                    className="w-[27px] h-[27px] flex items-center justify-center bg-white border border-[#DCDFE4] rounded-[6px] p-[6px]"
                    variant="ghost"
                    size="icon"
                    onClick={handleRemoveRule}
                >
                    <TrashIcon className="w-4 h-4 text-gray-700" />
                </Button>
            </div>
        </div>
    );
}
